import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WelcomeSection from './components/WelcomeSection';
import MyBikesSection from './components/MyBikesSection';
import RecentRecommendationsPanel from './components/RecentRecommendationsPanel';
import QuickUploadWidget from './components/QuickUploadWidget';
import NotificationBadges from './components/NotificationBadges';
import ActionButtonsSection from './components/ActionButtonsSection';

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [bikes, setBikes] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeDashboard = () => {
      try {
        // Check authentication
        const token = localStorage.getItem('authToken');
        const userProfile = localStorage.getItem('userProfile');
        
        if (!token || !userProfile) {
          navigate('/login');
          return;
        }

        const userData = JSON.parse(userProfile);
        setUser(userData);

        // Mock user stats and data
        const mockStats = {
          bikesAnalyzed: 3,
          recommendationsReceived: 24,
          moneySaved: 1250
        };

        const mockBikes = [
          {
            id: 1,
            name: "Trek Domane SL 6",
            type: "road",
            year: 2022,
            image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
            status: "analyzed",
            recommendationCount: 8,
            uploadDate: new Date(2025, 8, 1)
          },
          {
            id: 2,
            name: "Specialized Stumpjumper",
            type: "mountain",
            year: 2023,
            image: "https://images.unsplash.com/photo-1544191696-15693072e0e8?w=400&h=300&fit=crop",
            status: "analyzed",
            recommendationCount: 12,
            uploadDate: new Date(2025, 8, 3)
          },
          {
            id: 3,
            name: "Cannondale Quick CX",
            type: "hybrid",
            year: 2021,
            image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=400&h=300&fit=crop",
            status: "pending",
            recommendationCount: 0,
            uploadDate: new Date(2025, 8, 4)
          }
        ];

        const mockRecommendations = [
          {
            id: 1,
            productName: "Shimano Ultegra R8000 Groupset",
            category: "Drivetrain",
            bikeName: "Trek Domane SL 6",
            bikeId: 1,
            price: 899,
            priority: "high",
            performanceGain: 15,
            rating: 4.8,
            productImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop",
            purchaseUrl: "https://example.com/product/1",
            createdAt: new Date(2025, 8, 4, 10, 30)
          },
          {
            id: 2,
            productName: "Continental GP5000 Tires",
            category: "Wheels & Tires",
            bikeName: "Trek Domane SL 6",
            bikeId: 1,
            price: 129,
            priority: "medium",
            performanceGain: 8,
            rating: 4.9,
            productImage: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop",
            purchaseUrl: "https://example.com/product/2",
            createdAt: new Date(2025, 8, 4, 9, 15)
          },
          {
            id: 3,
            productName: "SRAM GX Eagle Derailleur",
            category: "Drivetrain",
            bikeName: "Specialized Stumpjumper",
            bikeId: 2,
            price: 245,
            priority: "high",
            performanceGain: 12,
            rating: 4.7,
            productImage: "https://images.unsplash.com/photo-1544191696-15693072e0e8?w=100&h=100&fit=crop",
            purchaseUrl: "https://example.com/product/3",
            createdAt: new Date(2025, 8, 3, 16, 45)
          },
          {
            id: 4,
            productName: "Fizik Antares R3 Saddle",
            category: "Saddle & Seatpost",
            bikeName: "Trek Domane SL 6",
            bikeId: 1,
            price: 179,
            priority: "low",
            performanceGain: 5,
            rating: 4.6,
            productImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&h=100&fit=crop",
            purchaseUrl: "https://example.com/product/4",
            createdAt: new Date(2025, 8, 3, 14, 20)
          },
          {
            id: 5,
            productName: "Mavic Cosmic Elite Wheels",
            category: "Wheels & Tires",
            bikeName: "Trek Domane SL 6",
            bikeId: 1,
            price: 549,
            priority: "medium",
            performanceGain: 10,
            rating: 4.5,
            productImage: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop",
            purchaseUrl: "https://example.com/product/5",
            createdAt: new Date(2025, 8, 2, 11, 30)
          }
        ];

        const mockNotifications = [
          {
            id: 1,
            type: "price-drop",
            title: "Price Drop Alert!",
            message: "Shimano Ultegra R8000 Groupset is now $50 cheaper",
            timestamp: new Date(2025, 8, 5, 8, 30),
            isRead: false,
            actionUrl: "/upgrade-recommendations?product=1",
            actionText: "View Deal"
          },
          {
            id: 2,
            type: "new-recommendation",
            title: "New Upgrade Available",
            message: "We found 3 new recommendations for your Trek Domane SL 6",
            timestamp: new Date(2025, 8, 4, 15, 45),
            isRead: false,
            actionUrl: "/upgrade-recommendations?bike=1",
            actionText: "View Recommendations"
          },
          {
            id: 3,
            type: "wishlist-available",
            title: "Wishlist Item Back in Stock",
            message: "Continental GP5000 Tires are now available",
            timestamp: new Date(2025, 8, 4, 12, 20),
            isRead: true,
            actionUrl: "/wishlist",
            actionText: "View Wishlist"
          },
          {
            id: 4,
            type: "system-update",
            title: "New Features Available",
            message: "Check out our improved bike analysis engine",
            timestamp: new Date(2025, 8, 3, 10, 0),
            isRead: true,
            actionUrl: "/photo-upload",
            actionText: "Try Now"
          }
        ];

        // Update user with stats
        const userWithStats = {
          ...userData,
          stats: mockStats
        };

        setUser(userWithStats);
        setBikes(mockBikes);
        setRecommendations(mockRecommendations);
        setNotifications(mockNotifications);

      } catch (error) {
        console.error('Error initializing dashboard:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Unable to load dashboard. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          {/* Welcome Section */}
          <WelcomeSection user={user} stats={user?.stats} />

          {/* Notifications */}
          <NotificationBadges notifications={notifications} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* My Bikes Section */}
              <MyBikesSection bikes={bikes} />

              {/* Recent Recommendations */}
              <RecentRecommendationsPanel recommendations={recommendations} />

              {/* Action Buttons - Mobile Only */}
              <div className="lg:hidden">
                <ActionButtonsSection />
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Quick Upload Widget - Always on top for mobile */}
              <div className="lg:order-1 order-first">
                <QuickUploadWidget />
              </div>

              {/* Action Buttons - Desktop Only */}
              <div className="hidden lg:block">
                <ActionButtonsSection />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;